import type { DocumentNode } from "graphql";
import { print } from "graphql";
import createFetchClient, { type Middleware } from "openapi-fetch";

import { getCredentialsHeaders } from "./credentials.ts";
import { getSdk } from "./graphql.ts";
import type { paths } from "./openapi.ts";
import { throwErrors } from "./throw-errors.ts";
import { urls } from "./urls.ts";

export interface Urls {
  /** Standard HTTP for REST and GraphQL */
  api: string;
  /** Web Sockets */
  ws: string;
  /** Application context path */
  basePath: string;
  /** External OpenAPI documentation */
  xapi: string;
}

export type ApiClient = ReturnType<typeof createApiClient>;

let globalClient: ApiClient | null = null;
let globalUrls: Urls | null = null;

const mainThreadMiddleware: Middleware = {
  async onRequest({ request }) {
    const headers = getCredentialsHeaders();

    Object.entries(headers).forEach(([key, value]) => {
      request.headers.set(key, value);
    });

    return request;
  },
  async onError({ error }) {
    throw error;
  },
};

export function createApiClient(urls: Urls, withMiddleware = true) {
  const client = createFetchClient<paths>({
    baseUrl: urls.api,
    querySerializer: {
      array: {
        style: "form",
        explode: false,
      },
    },
  });

  if (withMiddleware) {
    client.use(mainThreadMiddleware);
  }

  return client;
}

// Unlike Graphql, we need to set the urls in memory because the Web Workers,
// that use OpenAPI, do not have access to window.location.
export function initializeClient(urls: Urls, withMiddleware = true) {
  globalUrls = urls;
  globalClient = createApiClient(urls, withMiddleware);
  return globalClient;
}

export function getClient(): ApiClient {
  if (!globalClient) {
    throw new Error("Something wrong happened. Please refresh the page.");
  }

  return globalClient;
}

export function getUrls(): Urls {
  if (!globalUrls) {
    throw new Error("Something wrong happened. Please refresh the page.");
  }

  return globalUrls;
}

// *** GraphQL *** //

export const fetchGraphql = async <R, V>(doc: DocumentNode, variables?: V): Promise<R> => {
  const query = print(doc);

  const response = await fetch(urls.api + "/graphql", {
    method: "POST",
    headers: {
      ...getCredentialsHeaders(),
      "Content-Type": "application/json",
    },
    cache: "no-cache",
    body: JSON.stringify({
      query,
      variables,
    }),
  });
  const { data, errors } = await response.json();
  throwErrors(response, data, errors?.[0]?.message);
  return data;
};

export const graphql = getSdk(fetchGraphql);
